import React from "react";
import { CalendarIcon, TrendingUpIcon, TrendingDownIcon, MinusIcon, TagIcon, Eye } from "lucide-react";
import { Radio, Empty } from "antd";
import { Typography } from "antd";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;

interface BidTestItem {
  name: string;
  date: string;
  jobConfigId: string;
}

interface BidGroup {
  [key: string]: any;
  "Bid Value": number;
}

interface BidOutputData {
  highest: BidGroup[];
  medium: BidGroup[];
  lowest: BidGroup[];
  segment_columns: string[];
  total_records: number;
}

interface Props {
  bidTests?: BidTestItem[];
  selectedJobConfigId?: string | null;
  onSelect?: (jobConfigId: string) => void;
  bidOutputData?: BidOutputData | null;
}

const TILE_CONFIG = [
  {
    key: "highest" as const,
    label: "Highest Bid Values",
    Icon: TrendingUpIcon,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    valueBg: "bg-emerald-600 text-white",
  },
  {
    key: "medium" as const,
    label: "Medium Bid Values",
    Icon: MinusIcon,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    valueBg: "bg-blue-600 text-white",
  },
  {
    key: "lowest" as const,
    label: "Lowest Bid Values",
    Icon: TrendingDownIcon,
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    valueBg: "bg-orange-500 text-white",
  },
];

function formatColLabel(col: string): string {
  return col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function capitalizeFirst(val: any): string {
  const s = String(val);
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getBidRange(rows: BidGroup[]): string | null {
  const vals = rows
    .map((r) => r["Bid Value"])
    .filter((v) => typeof v === "number")
    .sort((a, b) => a - b);
  if (vals.length === 0) return null;
  if (vals.length === 1 || vals[0] === vals[vals.length - 1])
    return `${vals[0].toFixed(2)}`;
  return `${vals[0].toFixed(2)} – ${vals[vals.length - 1].toFixed(2)}`;
}

const BidDashboardCard: React.FC<Props> = ({
  bidTests = [],
  selectedJobConfigId,
  onSelect,
  bidOutputData,
}) => {
  const navigate = useNavigate();

  const selectedTest = bidTests.find((t) => t.jobConfigId === selectedJobConfigId);

  const handleView = (e: React.MouseEvent, test: BidTestItem) => {
    e.stopPropagation();
    navigate("/jobs/BidOptimizer", {
      state: { jobConfigId: test.jobConfigId, jobName: test.name, step: 3 },
    });
  };

  return (
    <div className="">
      {/* Header */}
      <div className="w-full mb-5 flex justify-center">
        <div className="bg-gradient-to-r from-indigo-50 via-white to-indigo-50 rounded-2xl shadow-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full border border-indigo-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 rounded-full flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <Title level={1} className="m-0">
                Bid Optimizer Results
              </Title>
              <p className="text-base mt-[-10px] text-gray-600">
                {selectedTest?.name || "No test selected"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/jobs/BidOptimizer")}
              className="px-4 py-2 bg-[#0055B8] text-white rounded-lg shadow hover:bg-[#003D82] transition"
            >
              Create a new job
            </button>
            <button
              onClick={() => navigate("/optimization/bid-optimizer")}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg shadow hover:bg-gray-200 transition"
            >
              View All
            </button>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Left — test list with radio buttons */}
        <div className="col-span-1 md:col-span-4 bg-white p-4 rounded-xl shadow-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Historical Bid Optimizer Results
          </h3>
          {bidTests.length === 0 ? (
            <Empty
              description="No completed tests available"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className="my-4"
              imageStyle={{ height: 40 }}
            />
          ) : (
            <div className="space-y-3">
              {bidTests.slice(0, 5).map((test) => (
                <div
                  key={test.jobConfigId}
                  className="flex items-center gap-3 border-b pb-3 cursor-pointer"
                  onClick={() => onSelect?.(test.jobConfigId)}
                >
                  <Radio checked={selectedJobConfigId === test.jobConfigId} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {test.name}
                    </p>
                    <div className="flex items-center text-xs text-gray-400 mt-1">
                      <CalendarIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span>{test.date}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleView(e, test)}
                    className="flex items-center gap-1 text-white text-xs px-3 py-1 rounded bg-[#0055B8] hover:bg-[#003D82] whitespace-nowrap flex-shrink-0"
                  >
                    <Eye className="w-3 h-3" />
                    View Results
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — three tiles stacked vertically */}
        <div className="col-span-1 md:col-span-8">
          {!bidOutputData ? (
            <div className="bg-white rounded-xl shadow-md h-full flex items-center justify-center py-16">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Data</h3>
                <p className="text-gray-500 text-sm">
                  Select a test to view bid multiplier results
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                {TILE_CONFIG.map(({ key, label, Icon, color, bg, border, valueBg }, tileIdx) => {
                  const rows: BidGroup[] = bidOutputData[key] || [];
                  const segCols: string[] = bidOutputData.segment_columns || [];
                  const bidRange = getBidRange(rows);

                  return (
                    <div
                      key={key}
                      className={`${bg} border-x border-b ${tileIdx === 0 ? "border-t rounded-t-xl" : ""} ${tileIdx === TILE_CONFIG.length - 1 ? "rounded-b-xl" : ""} ${border} px-4 pt-3 pb-4`}
                    >
                      {/* Tile header — label + bid range */}
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`w-4 h-4 ${color}`} />
                        <span className={`text-sm font-semibold ${color}`}>{label}</span>
                        {bidRange && (
                          <>
                            <span className="text-gray-300 text-xs mx-1">·</span>
                            <span className="text-xs text-gray-500 font-medium">
                              Bid Multiplier:
                            </span>
                            <span className={`${valueBg} text-xs font-bold px-2 py-0.5 rounded-md`}>
                              {bidRange}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Two segment cards side by side */}
                      <div className="grid grid-cols-2 gap-3">
                        {rows.length === 0 ? (
                          <p className="text-xs text-gray-400 col-span-2">No data</p>
                        ) : (
                          rows.map((row, i) => (
                            <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden">
                              {segCols.map((col) =>
                                row[col] !== undefined && row[col] !== null && row[col] !== "" ? (
                                  <div
                                    key={col}
                                    className="flex items-center justify-between px-3 py-1.5 border-b border-gray-50 last:border-b-0"
                                  >
                                    <span className="text-xs font-medium text-gray-400 flex items-center gap-1 flex-shrink-0">
                                      <TagIcon className="w-2.5 h-2.5 text-gray-300" />
                                      {formatColLabel(col)}
                                    </span>
                                    <span className="text-xs text-gray-800 font-semibold max-w-[52%] text-right truncate">
                                      {capitalizeFirst(row[col])}
                                    </span>
                                  </div>
                                ) : null
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Segment total — shown once, below all tiles */}
              <p className="text-xs text-gray-400 text-right mt-2 pr-1">
                {bidOutputData.total_records.toLocaleString()} total segments analyzed
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BidDashboardCard;
